import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useWatch, Controller, type Control } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { collection, query, where, orderBy } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { db } from "../../../lib/firebase";
import { useFirestoreCollectionOnce } from "../../../hooks/useFirestoreCollectionOnce";
import { useEvent } from "../../../layouts/EventLayout";
import { useEventVenue } from "../../../hooks/useEventVenue";
import { FormPageLayout } from "../../../components/forms/FormPageLayout";
import { FormSection } from "../../../components/forms/FormSection";
import { SchemaField, type FieldConfig } from "../../../components/forms/SchemaField";
import { AudiencePicker } from "../../../components/AudiencePicker";
import {
  LocationPicker,
  isPickerValueDirty,
  pickerValueStub,
  type LocationPickerValue,
} from "../../../components/forms/LocationPicker";
import { useLocationSaveConfirm } from "../../../components/forms/useLocationSaveConfirm";
import {
  useCreateTravelItem,
  useUpdateTravelItem,
} from "../../../hooks/useTravelMutations";
import { useSnackbar } from "../../../context/SnackbarContext";
import { useUnsavedChangesGuard } from "../../../hooks/useUnsavedChangesGuard";
import {
  travelFormSchema,
  type TravelFormData,
  type TravelDetailsFormData,
} from "../../../schemas/travelFormSchema";
import {
  dateTimeLocalToTimestamp,
  timestampToDateTimeLocal,
} from "../../../utils/timestamps";
import {
  AIRPORT_TIMEZONE_ENTRIES,
  requireAirportTimezone,
} from "@event-planner/shared";
import type {
  Session,
  TimestampLike,
  TransportMode,
  TravelItem,
  TravelItemDetails,
  TravelItemKind,
} from "@event-planner/shared";

const DEFAULT_TRAVEL_COLOR = "#2196F3";

const KIND_OPTIONS: Array<{ value: TravelItemKind; label: string }> = [
  { value: "flight", label: "Flight" },
  { value: "lodging", label: "Lodging" },
  { value: "transport", label: "Transport" },
  { value: "other", label: "Other" },
];

const TRANSPORT_MODE_OPTIONS: Array<{ value: TransportMode; label: string }> = [
  { value: "shuttle", label: "Shuttle" },
  { value: "car", label: "Car / hire" },
  { value: "rideshare", label: "Rideshare" },
  { value: "taxi", label: "Taxi" },
  { value: "train", label: "Train" },
  { value: "ferry", label: "Ferry" },
  { value: "walk", label: "Walk" },
  { value: "custom", label: "Custom" },
];

// ── Per-kind FieldConfig arrays ───────────────────────────────────────────

const FLIGHT_DIRECTION_OPTIONS = [
  { value: "inbound", label: "Inbound (arriving to event)" },
  { value: "outbound", label: "Outbound (leaving event)" },
];

const AIRPORT_OPTIONS = AIRPORT_TIMEZONE_ENTRIES.map(({ code, timezone }) => ({
  value: code,
  label: `${code} · ${timezone}`,
}));

const FLIGHT_FIELDS: FieldConfig<TravelFormData>[] = [
  {
    name: "details.direction",
    label: "Direction",
    type: "select",
    options: FLIGHT_DIRECTION_OPTIONS,
    helperText: "Which leg of the trip this is from the event's perspective.",
  },
  { name: "details.airline", label: "Airline", type: "text" },
  { name: "details.flightNumber", label: "Flight number", type: "text" },
  {
    name: "details.departureAirport",
    label: "Departure airport",
    type: "autocomplete",
    options: AIRPORT_OPTIONS,
    freeSolo: true,
    normalize: (value) => value.toUpperCase(),
    helperText: "IATA code; take off time is interpreted in this airport's timezone.",
    placeholder: "JFK",
  },
  { name: "details.departureTerminal", label: "Departure terminal", type: "text" },
  { name: "details.departureAt", label: "Departure time", type: "datetime-local" },
  {
    name: "details.arrivalAirport",
    label: "Arrival airport",
    type: "autocomplete",
    options: AIRPORT_OPTIONS,
    freeSolo: true,
    normalize: (value) => value.toUpperCase(),
    helperText: "IATA code; landing time is interpreted in this airport's timezone.",
    placeholder: "JAC",
  },
  { name: "details.arrivalTerminal", label: "Arrival terminal", type: "text" },
  { name: "details.arrivalAt", label: "Arrival time", type: "datetime-local" },
  { name: "details.confirmationCode", label: "Confirmation code", type: "text" },
  { name: "details.notes", label: "Notes", type: "textarea", rows: 2 },
];

const LODGING_FIELDS: FieldConfig<TravelFormData>[] = [
  { name: "details.checkInAt", label: "Check-in", type: "datetime-local" },
  { name: "details.checkOutAt", label: "Check-out", type: "datetime-local" },
  { name: "details.confirmationCode", label: "Confirmation code", type: "text" },
  { name: "details.notes", label: "Notes", type: "textarea", rows: 2 },
];

const TRANSPORT_FIELDS: FieldConfig<TravelFormData>[] = [
  {
    name: "details.mode",
    label: "Mode",
    type: "select",
    required: true,
    options: TRANSPORT_MODE_OPTIONS,
  },
  {
    name: "details.pickupAt",
    label: "Pickup time",
    type: "datetime-local",
    helperText:
      "Leave blank for open-ended vouchers (e.g. ride code valid throughout event).",
  },
  {
    name: "details.code",
    label: "Code (rideshare voucher / confirmation)",
    type: "text",
  },
  {
    name: "details.voucherUrl",
    label: "Voucher link",
    type: "text",
    helperText:
      "Optional Uber voucher claim link from the Uber Business dashboard.",
  },
  {
    name: "details.spendLimitAmount",
    label: "Spend limit amount",
    type: "number",
    helperText:
      "Optional cap on the voucher's value. Leave blank if uncapped.",
  },
  {
    name: "details.spendLimitCurrency",
    label: "Spend limit currency",
    type: "text",
    helperText: "3-letter ISO code (e.g. USD).",
  },
  {
    name: "details.validityFrom",
    label: "Valid from",
    type: "datetime-local",
    helperText: "Optional — when an open-ended voucher becomes usable.",
  },
  {
    name: "details.validityUntil",
    label: "Valid until",
    type: "datetime-local",
    helperText: "Optional — when an open-ended voucher expires.",
  },
  { name: "details.notes", label: "Notes", type: "textarea", rows: 2 },
];

const OTHER_FIELDS: FieldConfig<TravelFormData>[] = [
  { name: "details.title", label: "Title", type: "text", required: true },
  { name: "details.description", label: "Description", type: "textarea", rows: 2 },
  { name: "details.time", label: "Time", type: "datetime-local" },
  { name: "details.notes", label: "Notes", type: "textarea", rows: 2 },
];

const REMINDER_TOGGLE_FIELD: FieldConfig<TravelFormData> = {
  name: "reminder.enabled",
  label: "Send a push reminder before this travel time",
  type: "switch",
};

const REMINDER_LEAD_FIELD: FieldConfig<TravelFormData> = {
  name: "reminder.leadMinutes",
  label: "Lead time (minutes)",
  type: "number",
  required: true,
  helperText: "How many minutes before to fire",
};

const REMINDER_BODY_FIELD: FieldConfig<TravelFormData> = {
  name: "reminder.body",
  label: "Reminder body",
  type: "textarea",
  rows: 3,
  required: true,
  helperText: "Sent verbatim as the push notification body",
};

const FIELDS_BY_KIND: Record<TravelItemKind, FieldConfig<TravelFormData>[]> = {
  flight: FLIGHT_FIELDS,
  lodging: LODGING_FIELDS,
  transport: TRANSPORT_FIELDS,
  other: OTHER_FIELDS,
};

const KIND_TITLE: Record<TravelItemKind, string> = {
  flight: "Flight",
  lodging: "Lodging",
  transport: "Transport",
  other: "Other travel",
};

interface TravelFormProps {
  mode: "create" | "edit";
  initialData?: TravelItem;
  /** Audience IDs to pre-load into the picker on edit. */
  initialPickedAudienceIds?: string[];
}

function emptyDetails(kind: TravelItemKind): TravelDetailsFormData {
  switch (kind) {
    case "flight":
      return {
        kind: "flight",
        direction: null,
        airline: null,
        flightNumber: null,
        departureAirport: null,
        departureAt: null,
        departureTerminal: null,
        arrivalAirport: null,
        arrivalAt: null,
        arrivalTerminal: null,
        confirmationCode: null,
        notes: null,
      };
    case "lodging":
      return {
        kind: "lodging",
        checkInAt: null,
        checkOutAt: null,
        confirmationCode: null,
        notes: null,
      };
    case "transport":
      return {
        kind: "transport",
        mode: "shuttle",
        pickupAt: null,
        code: null,
        voucherUrl: null,
        spendLimitAmount: null,
        spendLimitCurrency: null,
        validityFrom: null,
        validityUntil: null,
        notes: null,
      };
    case "other":
      return {
        kind: "other",
        title: "",
        description: null,
        time: null,
        notes: null,
      };
  }
}

/**
 * Convert TimestampLike date fields on `TravelItem.details` to ISO strings
 * for the form. Mirrors SessionForm's `timestampToDateTimeLocal` calls in
 * `buildDefaultValues`.
 */
function detailsToFormData(
  details: TravelItemDetails,
  timezone: string,
): TravelDetailsFormData {
  const t = (v: TimestampLike | null) =>
    v ? timestampToDateTimeLocal(v, timezone) : null;
  switch (details.kind) {
    case "flight":
      return {
        ...details,
        departureAt: details.departureAt
          ? timestampToDateTimeLocal(
              details.departureAt,
              requireAirportTimezone(
                details.departureAirport,
                "departure",
              ),
            )
          : null,
        arrivalAt: details.arrivalAt
          ? timestampToDateTimeLocal(
              details.arrivalAt,
              requireAirportTimezone(details.arrivalAirport, "arrival"),
            )
          : null,
      };
    case "lodging":
      return {
        ...details,
        checkInAt: t(details.checkInAt),
        checkOutAt: t(details.checkOutAt),
      };
    case "transport":
      return {
        ...details,
        pickupAt: t(details.pickupAt),
        voucherUrl: details.voucherUrl ?? null,
        spendLimitAmount: details.spendLimit?.amount ?? null,
        spendLimitCurrency: details.spendLimit?.currency ?? null,
        validityFrom: t(details.validityWindow?.from ?? null),
        validityUntil: t(details.validityWindow?.to ?? null),
      };
    case "other":
      return { ...details, time: t(details.time) };
  }
}

// Defaults for the always-present form reminder shape. Submit collapses
// to null when `enabled === false` so the doc shape (`reminder | null`)
// is unchanged.
const REMINDER_DEFAULTS = { enabled: false, leadMinutes: 60, body: "" };

function buildDefaultValues(
  initialData: TravelItem | undefined,
  initialPickedAudienceIds: string[],
  timezone: string,
): TravelFormData {
  if (initialData) {
    return {
      details: detailsToFormData(initialData.details, timezone),
      pickupLocationId: initialData.pickupLocationId,
      dropoffLocationId: initialData.dropoffLocationId,
      pickedAudienceIds: initialPickedAudienceIds,
      sessionLink: initialData.sessionLink,
      reminder: initialData.reminder ?? REMINDER_DEFAULTS,
    };
  }
  return {
    details: emptyDetails("flight"),
    pickupLocationId: null,
    dropoffLocationId: null,
    pickedAudienceIds: [],
    sessionLink: null,
    reminder: REMINDER_DEFAULTS,
  };
}

export function TravelForm({
  mode,
  initialData,
  initialPickedAudienceIds = [],
}: TravelFormProps) {
  const { eventId } = useParams<{ eventId: string }>();
  const { event } = useEvent();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { create, loading: creating } = useCreateTravelItem();
  const { update, loading: updating } = useUpdateTravelItem();
  const [serverError, setServerError] = useState<string | null>(null);

  const timezone = event?.timezone ?? "America/New_York";

  const form = useForm<TravelFormData>({
    resolver: zodResolver(travelFormSchema),
    mode: "onChange",
    defaultValues: buildDefaultValues(
      initialData,
      initialPickedAudienceIds,
      timezone,
    ),
  });

  // Pickup / dropoff LocationPicker values per the named-slot schema:
  //   flight     → no location pickers
  //   lodging    → dropoffLocationId = hotel
  //   transport  → pickupLocationId + dropoffLocationId
  //   other      → dropoffLocationId = the single location
  const [pickupPickerValue, setPickupPickerValue] =
    useState<LocationPickerValue>(
      initialData?.pickupLocationId
        ? pickerValueStub(initialData.pickupLocationId, DEFAULT_TRAVEL_COLOR)
        : null,
    );
  const [dropoffPickerValue, setDropoffPickerValue] =
    useState<LocationPickerValue>(
      initialData?.dropoffLocationId
        ? pickerValueStub(initialData.dropoffLocationId, DEFAULT_TRAVEL_COLOR)
        : null,
    );

  // Form-level dirty gates for both LocationPickers. See EventForm
  // for the architecture rationale.
  const pickupBaselineRef = useRef<LocationPickerValue>(
    initialData?.pickupLocationId
      ? pickerValueStub(initialData.pickupLocationId, DEFAULT_TRAVEL_COLOR)
      : null,
  );
  const dropoffBaselineRef = useRef<LocationPickerValue>(
    initialData?.dropoffLocationId
      ? pickerValueStub(initialData.dropoffLocationId, DEFAULT_TRAVEL_COLOR)
      : null,
  );
  useEffect(() => {
    const baseline = pickupBaselineRef.current;
    if (
      baseline?.locationId &&
      baseline.locationId === pickupPickerValue?.locationId &&
      !baseline.label &&
      pickupPickerValue?.label
    ) {
      pickupBaselineRef.current = pickupPickerValue;
    }
  }, [pickupPickerValue]);
  useEffect(() => {
    const baseline = dropoffBaselineRef.current;
    if (
      baseline?.locationId &&
      baseline.locationId === dropoffPickerValue?.locationId &&
      !baseline.label &&
      dropoffPickerValue?.label
    ) {
      dropoffBaselineRef.current = dropoffPickerValue;
    }
  }, [dropoffPickerValue]);

  const { venue: eventVenue } = useEventVenue(event);
  const pickerBiasCoordinate = eventVenue?.coordinates ?? undefined;

  const sessionsQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, "sessions"),
            where("eventId", "==", eventId),
            orderBy("date", "asc"),
            orderBy("startTime", "asc"),
          )
        : null,
    [eventId],
  );
  const { data: sessions } = useFirestoreCollectionOnce<Session>(sessionsQuery);

  const detailsKind = useWatch({
    control: form.control,
    name: "details.kind",
  }) as TravelItemKind;

  const reminderEnabled = useWatch({
    control: form.control,
    name: "reminder.enabled",
  });

  const sessionLinkOptions = useMemo(
    () => [
      { value: "", label: "— none —" },
      ...sessions.map((s) => ({ value: s.id, label: s.title })),
    ],
    [sessions],
  );

  const SESSION_LINK_FIELD: FieldConfig<TravelFormData> = useMemo(
    () => ({
      name: "sessionLink",
      label: "For which session?",
      type: "select",
      options: sessionLinkOptions,
      helperText:
        "Optional UI affordance — adds a 'for: <session>' badge to the travel card. Distinct from the audience above (audience drives delivery).",
    }),
    [sessionLinkOptions],
  );
  const canLinkSession = detailsKind !== "lodging";
  const submitting = creating || updating;

  const { dialog: unsavedGuardDialog } = useUnsavedChangesGuard(
    form.formState.isDirty,
    form.formState.isSubmitting || submitting,
  );

  function handleKindChange(kind: TravelItemKind) {
    form.setValue("details", emptyDetails(kind));
    // Reset pickup/dropoff slots when switching kinds, since the schema
    // for what each slot means is kind-dependent (transport uses pickup;
    // lodging/other put their single location into dropoff).
    setPickupPickerValue(null);
    setDropoffPickerValue(null);
    form.setValue("pickupLocationId", null);
    form.setValue("dropoffLocationId", null);
  }

  // Two pickers (Pickup + Dropoff for transport, etc.) share one
  // confirm hook — the dialog renders for whichever slot triggers it.
  const { confirmAndResolve, dialog: locationSaveConfirmDialog } =
    useLocationSaveConfirm();

  async function onSubmit(data: TravelFormData) {
    setServerError(null);
    try {
      // Form holds ISO date strings; service layer expects TimestampLike.
      // Convert at the boundary, mirroring SessionForm.
      const detailsForWrite = formDetailsToDocDetails(data.details, timezone);
      // Form holds reminder always-present; collapse to null on disabled
      // so the persisted doc shape stays clean.
      const reminder = data.reminder.enabled ? data.reminder : null;
      const sessionLink = canLinkSession ? data.sessionLink : null;

      // Resolve pickup picker (transport only — null for other kinds).
      let pickupLocationId: string | null = null;
      if (data.details.kind === "transport") {
        if (
          mode === "edit" &&
          !isPickerValueDirty(pickupBaselineRef.current, pickupPickerValue)
        ) {
          pickupLocationId = initialData?.pickupLocationId ?? null;
        } else {
          const resolved = await confirmAndResolve(
            pickupPickerValue,
            eventId!,
          );
          if (resolved === undefined) return;
          pickupLocationId = resolved;
        }
      }

      // Resolve dropoff picker (transport / lodging / other).
      let dropoffLocationId: string | null = null;
      if (data.details.kind !== "flight") {
        if (
          mode === "edit" &&
          !isPickerValueDirty(dropoffBaselineRef.current, dropoffPickerValue)
        ) {
          dropoffLocationId = initialData?.dropoffLocationId ?? null;
        } else {
          const resolved = await confirmAndResolve(
            dropoffPickerValue,
            eventId!,
          );
          if (resolved === undefined) return;
          dropoffLocationId = resolved;
        }
      }

      if (mode === "create") {
        await create({
          eventId: eventId!,
          details: detailsForWrite,
          pickupLocationId,
          dropoffLocationId,
          pickedAudienceIds: data.pickedAudienceIds,
          sessionLink,
          reminder,
          createdByUserId: user?.uid ?? "",
        });
        showSuccess("Travel item created");
      } else {
        await update(initialData!.id, eventId!, {
          details: detailsForWrite,
          pickupLocationId,
          dropoffLocationId,
          pickedAudienceIds: data.pickedAudienceIds,
          sessionLink,
          reminder,
        });
        showSuccess("Travel item updated");
      }
      navigate(`/events/${eventId}/travel`);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to save travel item";
      setServerError(message);
      showError(message);
    }
  }

  const detailFields = FIELDS_BY_KIND[detailsKind];

  // Per-kind dropoff label so the picker reads as "Hotel" / "Location"
  // rather than always "Dropoff".
  const dropoffSectionTitle =
    detailsKind === "transport"
      ? "Dropoff Location"
      : detailsKind === "lodging"
        ? "Hotel"
        : "Location";

  return (
    <>
      {locationSaveConfirmDialog}
      {unsavedGuardDialog}
      <FormPageLayout
        title={mode === "create" ? "New Travel Item" : "Edit Travel Item"}
        onSubmit={form.handleSubmit(onSubmit)}
      error={serverError}
      submitting={submitting}
      submitLabel={mode === "create" ? "Create" : "Save Changes"}
      testIdSlug="travel"
    >
      <FormSection title="Type">
        <SchemaField
          control={form.control}
          config={{
            name: "details.kind",
            label: "Travel type",
            type: "select",
            required: true,
            options: KIND_OPTIONS,
          }}
        />
      </FormSection>

      <FormSection title={KIND_TITLE[detailsKind]}>
        {detailFields.map((field) => (
          <SchemaField
            key={String(field.name)}
            config={field}
            control={form.control}
          />
        ))}
      </FormSection>

      {detailsKind === "transport" && (
        <FormSection title="Pickup Location">
          <LocationPicker
            eventId={eventId!}
            value={pickupPickerValue}
            onChange={setPickupPickerValue}
            defaultColor={DEFAULT_TRAVEL_COLOR}
            testIdSuffix="travel-pickup"
            mapHeight={160}
            searchBiasCoordinate={pickerBiasCoordinate}
          />
        </FormSection>
      )}

      {detailsKind !== "flight" && (
        <FormSection title={dropoffSectionTitle}>
          <LocationPicker
            eventId={eventId!}
            value={dropoffPickerValue}
            onChange={setDropoffPickerValue}
            defaultColor={DEFAULT_TRAVEL_COLOR}
            testIdSuffix={`travel-dropoff-${detailsKind}`}
            mapHeight={160}
            searchBiasCoordinate={pickerBiasCoordinate}
          />
        </FormSection>
      )}

      <FormSection title="Travelers">
        <Controller
          control={form.control}
          name="pickedAudienceIds"
          render={({ field }) => (
            <AudiencePicker
              eventId={eventId}
              value={field.value ?? []}
              onChange={field.onChange}
              label="Travelers"
              placeholder="Pick groups or specific people…"
              helperText="Selected groups and people get this travel item and any reminders."
              includePendingContacts
              testId="travel-audience"
              required
            />
          )}
        />
      </FormSection>

      {canLinkSession && (
        <FormSection title="Linked session (optional)">
          <SchemaField control={form.control} config={SESSION_LINK_FIELD} />
        </FormSection>
      )}

      <FormSection title="Reminder">
        <SchemaField control={form.control} config={REMINDER_TOGGLE_FIELD} />
        {reminderEnabled && (
          <>
            <SchemaField control={form.control} config={REMINDER_LEAD_FIELD} />
            <SchemaField control={form.control} config={REMINDER_BODY_FIELD} />
          </>
        )}
      </FormSection>

      {/* Type-change side effect: replace `details` with the empty shape
          for the new kind so stale per-kind fields don't bleed across.
          Controlled via SchemaField's onChange isn't available, so we
          listen on details.kind and rebuild via setValue. */}
      <DetailsKindWatcher
        control={form.control}
        currentKind={detailsKind}
        onChange={handleKindChange}
      />
      </FormPageLayout>
    </>
  );
}

// ── Type-change side effect helper ────────────────────────────────────────
//
// SchemaField doesn't surface an onChange callback; details.kind needs to
// trigger a full `details` rebuild when it flips so stale per-kind fields
// don't linger in form state. This headless component watches the value
// and fires the rebuild via setValue.

function DetailsKindWatcher({
  control,
  currentKind,
  onChange,
}: {
  control: Control<TravelFormData>;
  currentKind: TravelItemKind;
  onChange: (k: TravelItemKind) => void;
}) {
  const watched = useWatch({ control, name: "details.kind" }) as TravelItemKind;
  const prev = useRef(currentKind);
  useEffect(() => {
    if (watched !== prev.current) {
      onChange(watched);
      prev.current = watched;
    }
  }, [watched, onChange]);
  return null;
}

// ── Form → doc shape conversion ───────────────────────────────────────────

function formDetailsToDocDetails(
  details: TravelDetailsFormData,
  timezone: string,
): TravelItemDetails {
  const t = (v: string | null): TimestampLike | null =>
    v ? dateTimeLocalToTimestamp(v, timezone) : null;
  switch (details.kind) {
    // Braced like the `transport` case below: `const` in an unbraced case body
    // is scoped to the whole switch, so it is visible (in the TDZ) from the
    // other arms. Same fix, same reason.
    case "flight": {
      const departureAirport =
        details.departureAirport?.trim().toUpperCase() || null;
      const arrivalAirport =
        details.arrivalAirport?.trim().toUpperCase() || null;
      return {
        ...details,
        departureAirport,
        departureAt: details.departureAt
          ? dateTimeLocalToTimestamp(
              details.departureAt,
              requireAirportTimezone(
                departureAirport,
                "departure",
              ),
            )
          : null,
        arrivalAirport,
        arrivalAt: details.arrivalAt
          ? dateTimeLocalToTimestamp(
              details.arrivalAt,
              requireAirportTimezone(arrivalAirport, "arrival"),
            )
          : null,
      };
    }
    case "lodging":
      return {
        ...details,
        checkInAt: t(details.checkInAt),
        checkOutAt: t(details.checkOutAt),
      };
    case "transport": {
      const spendLimit =
        details.spendLimitAmount !== null && details.spendLimitCurrency
          ? {
              amount: details.spendLimitAmount,
              currency: details.spendLimitCurrency,
            }
          : null;
      const fromTs = t(details.validityFrom);
      const toTs = t(details.validityUntil);
      const validityWindow =
        fromTs && toTs ? { from: fromTs, to: toTs } : null;
      return {
        kind: "transport",
        mode: details.mode,
        pickupAt: t(details.pickupAt),
        code: details.code,
        voucherUrl: details.voucherUrl || null,
        spendLimit,
        validityWindow,
        notes: details.notes,
      };
    }
    case "other":
      return { ...details, time: t(details.time) };
  }
}
